import { ACCOUNT_ROUTE } from "@/common/static/routes.static";
import type { ContentLocale } from "@/i18n/config";

/** WHO-5959 6b. Pro Buyer-projecten, under Kopen in My Account (spec S1). */
export const ACCOUNT_PRO_PROJECTS_ROUTE = `${ACCOUNT_ROUTE}/pro-buyer-projecten`;

export const proProjectRoute = (id: string) =>
  `${ACCOUNT_PRO_PROJECTS_ROUTE}/${encodeURIComponent(id)}`;

/** Same limit the gateway enforces (PROJECT_NAME_MAX_LENGTH). */
export const PRO_PROJECT_NAME_MAX_LENGTH = 40;

/** Spec S2: the overview loads 24 projects at a time. */
export const PRO_PROJECTS_PAGE_SIZE = 24;

/** Spec S2: up to 3 product photos per project card. */
export const PRO_PROJECT_CARD_PHOTOS = 3;

/** Spec S3: the filter shows when a project has more than 5 purchases. */
export const PRO_PROJECT_FILTER_MIN_PURCHASES = 6;

/** Spec S1: the pink "Nieuw" pill shows for 30 days after the item first appears for an account. */
export const PRO_PROJECTS_NEW_LABEL_DAYS = 30;
export const PRO_PROJECTS_FIRST_SEEN_STORAGE_KEY =
  "whoppah:pro-projects-menu-first-seen";

/** WHO-5959 6d: the gateway puts at most this many invoices in one zip (Thomas's cap). */
export const PRO_PROJECT_INVOICE_ZIP_MAX = 50;

/** Gateway error codes from `pro-buyer-project.graphql`. */
export const PRO_PROJECT_ERROR_CODE = {
  nameInvalid: "PROJECT_NAME_INVALID",
  nameTaken: "PROJECT_NAME_TAKEN",
  nameContactDetails: "PROJECT_NAME_CONTACT_DETAILS",
} as const;

/** Per content language; read through `t()`, with `.en` as the fallback. */
type Locales = Record<ContentLocale, string>;

/**
 * Every string of the tab, in the six web languages. Lokalise holds the same
 * values in `account.json` under these keys (WHO-5959 6b seed); the English
 * text is the in-code fallback. Dutch follows Evelien's spec word for word.
 * "Pro Buyer" stays untranslated, and no copy here uses an em dash.
 */
export const PRO_PROJECTS_COPY = {
  menuItem: {
    key: "pro-projects-menu-item",
    en: "Pro Buyer projects",
    nl: "Pro Buyer-projecten",
    de: "Pro Buyer-Projekte",
    fr: "Projets Pro Buyer",
    es: "Proyectos Pro Buyer",
    it: "Progetti Pro Buyer",
  },
  newLabel: {
    key: "pro-projects-new-label",
    en: "New",
    nl: "Nieuw",
    de: "Neu",
    fr: "Nouveau",
    es: "Nuevo",
    it: "Nuovo",
  },
  intro: {
    key: "pro-projects-intro",
    en: "Keep track per client of what you bought and reserved. Only you see your projects.",
    nl: "Houd per klant bij wat je kocht en reserveerde. Alleen jij ziet je projecten.",
    de: "Behalte pro Kunde im Blick, was du gekauft und reserviert hast. Nur du siehst deine Projekte.",
    fr: "Suivez par client ce que vous avez acheté et réservé. Vous seul voyez vos projets.",
    es: "Controla por cliente lo que has comprado y reservado. Solo tú ves tus proyectos.",
    it: "Tieni traccia per cliente di ciò che hai acquistato e prenotato. Solo tu vedi i tuoi progetti.",
  },
  /* WHO-6629 F11: the intro once favorites can sit in projects (workspace gate). */
  introWithFavorites: {
    key: "pro-projects-intro-favorites",
    en: "Keep your favorites, purchases and reservations per client. Only you see your projects.",
    nl: "Bewaar per klant je favorieten, aankopen en reserveringen. Alleen jij ziet je projecten.",
    de: "Bewahre pro Kunde deine Favoriten, Käufe und Reservierungen auf. Nur du siehst deine Projekte.",
    fr: "Gardez par client vos favoris, achats et réservations. Vous seul voyez vos projets.",
    es: "Guarda por cliente tus favoritos, compras y reservas. Solo tú ves tus proyectos.",
    it: "Conserva per cliente i tuoi preferiti, acquisti e prenotazioni. Solo tu vedi i tuoi progetti.",
  },
  newProject: {
    key: "pro-projects-new",
    en: "New project",
    nl: "Nieuw project",
    de: "Neues Projekt",
    fr: "Nouveau projet",
    es: "Nuevo proyecto",
    it: "Nuovo progetto",
  },
  emptyBody: {
    key: "pro-projects-empty-body",
    en: "No projects yet. Create a project for a client and add your purchases.",
    nl: "Nog geen projecten. Maak een project voor een klant en voeg je aankopen toe.",
    de: "Noch keine Projekte. Erstelle ein Projekt für einen Kunden und füge deine Käufe hinzu.",
    fr: "Pas encore de projets. Créez un projet pour un client et ajoutez vos achats.",
    es: "Aún no hay proyectos. Crea un proyecto para un cliente y añade tus compras.",
    it: "Ancora nessun progetto. Crea un progetto per un cliente e aggiungi i tuoi acquisti.",
  },
  purchaseCountOne: {
    key: "pro-projects-purchase-count-one",
    en: "{{count}} purchase",
    nl: "{{count}} aankoop",
    de: "{{count}} Kauf",
    fr: "{{count}} achat",
    es: "{{count}} compra",
    it: "{{count}} acquisto",
  },
  purchaseCount: {
    key: "pro-projects-purchase-count",
    en: "{{count}} purchases",
    nl: "{{count}} aankopen",
    de: "{{count}} Käufe",
    fr: "{{count}} achats",
    es: "{{count}} compras",
    it: "{{count}} acquisti",
  },
  figureTotalSpent: {
    key: "pro-projects-figure-total-spent",
    en: "Total spent",
    nl: "Totaal besteed",
    de: "Insgesamt ausgegeben",
    fr: "Total dépensé",
    es: "Total gastado",
    it: "Totale speso",
  },
  figureProBuyerSpent: {
    key: "pro-projects-figure-pro-buyer-spent",
    en: "At Pro Buyer price",
    nl: "Met Pro Buyer-prijs",
    de: "Mit Pro Buyer-Preis",
    fr: "Au prix Pro Buyer",
    es: "Con precio Pro Buyer",
    it: "Con prezzo Pro Buyer",
  },
  figureShare: {
    key: "pro-projects-figure-share",
    en: "{{pct}}% of your purchases",
    nl: "{{pct}}% van je aankopen",
    de: "{{pct}}% deiner Käufe",
    fr: "{{pct}}% de vos achats",
    es: "{{pct}}% de tus compras",
    it: "{{pct}}% dei tuoi acquisti",
  },
  saved: {
    key: "pro-projects-saved",
    en: "Saved with Pro Buyer",
    nl: "Bespaard met Pro Buyer",
    de: "Mit Pro Buyer gespart",
    fr: "Économisé avec Pro Buyer",
    es: "Ahorrado con Pro Buyer",
    it: "Risparmiato con Pro Buyer",
  },
  figureAverageDiscount: {
    key: "pro-projects-figure-average-discount",
    en: "on average {{pct}}% off",
    nl: "gemiddeld {{pct}}% korting",
    de: "im Schnitt {{pct}}% Rabatt",
    fr: "en moyenne {{pct}}% de remise",
    es: "de media un {{pct}}% de descuento",
    it: "in media {{pct}}% di sconto",
  },
  figureReserved: {
    key: "pro-projects-figure-reserved",
    en: "Reserved",
    nl: "Gereserveerd",
    de: "Reserviert",
    fr: "Réservé",
    es: "Reservado",
    it: "Prenotato",
  },
  figureAwaitingSeller: {
    key: "pro-projects-figure-awaiting-seller",
    en: "{{count}} waiting for the seller",
    nl: "{{count}} wacht op de verkoper",
    de: "{{count}} wartet auf den Verkäufer",
    fr: "{{count}} en attente du vendeur",
    es: "{{count}} esperando al vendedor",
    it: "{{count}} in attesa del venditore",
  },
  spent: {
    key: "pro-projects-spent",
    en: "Spent",
    nl: "Besteed",
    de: "Ausgegeben",
    fr: "Dépensé",
    es: "Gastado",
    it: "Speso",
  },
  savedShort: {
    key: "pro-projects-saved-short",
    en: "Saved",
    nl: "Bespaard",
    de: "Gespart",
    fr: "Économisé",
    es: "Ahorrado",
    it: "Risparmiato",
  },
  cardNoPurchases: {
    key: "pro-projects-card-no-purchases",
    en: "No purchases yet",
    nl: "Nog geen aankopen",
    de: "Noch keine Käufe",
    fr: "Pas encore d'achats",
    es: "Aún no hay compras",
    it: "Ancora nessun acquisto",
  },
  looseTitle: {
    key: "pro-projects-loose-title",
    en: "Without a project",
    nl: "Zonder project",
    de: "Ohne Projekt",
    fr: "Sans projet",
    es: "Sin proyecto",
    it: "Senza progetto",
  },
  looseBody: {
    key: "pro-projects-loose-body",
    en: "{{count}} purchases without a project",
    nl: "{{count}} aankopen zonder project",
    de: "{{count}} Käufe ohne Projekt",
    fr: "{{count}} achats sans projet",
    es: "{{count}} compras sin proyecto",
    it: "{{count}} acquisti senza progetto",
  },
  looseAction: {
    key: "pro-projects-loose-action",
    en: "Add to a project",
    nl: "Aan een project toevoegen",
    de: "Zu einem Projekt hinzufügen",
    fr: "Ajouter à un projet",
    es: "Añadir a un proyecto",
    it: "Aggiungi a un progetto",
  },
  loadMore: {
    key: "pro-projects-load-more",
    en: "Load more",
    nl: "Meer laden",
    de: "Mehr laden",
    fr: "Charger plus",
    es: "Cargar más",
    it: "Carica altri",
  },
  createdOn: {
    key: "pro-projects-created-on",
    en: "Created on {{date}}",
    nl: "Aangemaakt op {{date}}",
    de: "Erstellt am {{date}}",
    fr: "Créé le {{date}}",
    es: "Creado el {{date}}",
    it: "Creato il {{date}}",
  },
  tabOverview: {
    key: "pro-projects-tab-overview",
    en: "Overview",
    nl: "Overzicht",
    de: "Übersicht",
    fr: "Aperçu",
    es: "Resumen",
    it: "Panoramica",
  },
  actions: {
    key: "pro-projects-actions",
    en: "Project actions",
    nl: "Projectacties",
    de: "Projektaktionen",
    fr: "Actions du projet",
    es: "Acciones del proyecto",
    it: "Azioni del progetto",
  },
  rename: {
    key: "pro-projects-rename",
    en: "Rename",
    nl: "Naam wijzigen",
    de: "Umbenennen",
    fr: "Renommer",
    es: "Cambiar nombre",
    it: "Rinomina",
  },
  delete: {
    key: "pro-projects-delete",
    en: "Delete project",
    nl: "Project verwijderen",
    de: "Projekt löschen",
    fr: "Supprimer le projet",
    es: "Eliminar proyecto",
    it: "Elimina progetto",
  },
  deleteTitle: {
    key: "pro-projects-delete-title",
    en: "Delete project?",
    nl: "Project verwijderen?",
    de: "Projekt löschen?",
    fr: "Supprimer le projet ?",
    es: "¿Eliminar proyecto?",
    it: "Eliminare il progetto?",
  },
  deleteBody: {
    key: "pro-projects-delete-body",
    en: "Your purchases stay in Purchases and your invoices do not change.",
    nl: "Je aankopen blijven in Aankopen staan en je facturen veranderen niet.",
    de: "Deine Käufe bleiben unter Käufe und deine Rechnungen ändern sich nicht.",
    fr: "Vos achats restent dans Achats et vos factures ne changent pas.",
    es: "Tus compras siguen en Compras y tus facturas no cambian.",
    it: "I tuoi acquisti restano in Acquisti e le tue fatture non cambiano.",
  },
  deleteConfirm: {
    key: "pro-projects-delete-confirm",
    en: "Delete",
    nl: "Verwijderen",
    de: "Löschen",
    fr: "Supprimer",
    es: "Eliminar",
    it: "Elimina",
  },
  cancel: {
    key: "pro-projects-cancel",
    en: "Cancel",
    nl: "Annuleren",
    de: "Abbrechen",
    fr: "Annuler",
    es: "Cancelar",
    it: "Annulla",
  },
  save: {
    key: "pro-projects-save",
    en: "Save",
    nl: "Opslaan",
    de: "Speichern",
    fr: "Enregistrer",
    es: "Guardar",
    it: "Salva",
  },
  createConfirm: {
    key: "pro-projects-create-confirm",
    en: "Create project",
    nl: "Project maken",
    de: "Projekt erstellen",
    fr: "Créer le projet",
    es: "Crear proyecto",
    it: "Crea progetto",
  },
  nameLabel: {
    key: "pro-projects-name-label",
    en: "Project name",
    nl: "Naam van het project",
    de: "Name des Projekts",
    fr: "Nom du projet",
    es: "Nombre del proyecto",
    it: "Nome del progetto",
  },
  nameHelp: {
    key: "pro-projects-name-help",
    en: "For example your client's name or the address. Maximum 40 characters.",
    nl: "Bijvoorbeeld de naam van je klant of het adres. Maximaal 40 tekens.",
    de: "Zum Beispiel der Name deines Kunden oder die Adresse. Maximal 40 Zeichen.",
    fr: "Par exemple le nom de votre client ou l'adresse. 40 caractères maximum.",
    es: "Por ejemplo, el nombre de tu cliente o la dirección. Máximo 40 caracteres.",
    it: "Ad esempio il nome del tuo cliente o l'indirizzo. Massimo 40 caratteri.",
  },
  errorNameEmpty: {
    key: "pro-projects-error-name-empty",
    en: "Give your project a name.",
    nl: "Geef je project een naam.",
    de: "Gib deinem Projekt einen Namen.",
    fr: "Donnez un nom à votre projet.",
    es: "Ponle un nombre a tu proyecto.",
    it: "Dai un nome al tuo progetto.",
  },
  errorNameInvalid: {
    key: "pro-projects-error-name-invalid",
    en: "Use at most 40 characters.",
    nl: "Gebruik maximaal 40 tekens.",
    de: "Verwende höchstens 40 Zeichen.",
    fr: "Utilisez 40 caractères maximum.",
    es: "Usa como máximo 40 caracteres.",
    it: "Usa al massimo 40 caratteri.",
  },
  errorNameTaken: {
    key: "pro-projects-error-name-taken",
    en: "You already have a project with this name.",
    nl: "Je hebt al een project met deze naam.",
    de: "Du hast bereits ein Projekt mit diesem Namen.",
    fr: "Vous avez déjà un projet portant ce nom.",
    es: "Ya tienes un proyecto con este nombre.",
    it: "Hai già un progetto con questo nome.",
  },
  errorNameContact: {
    key: "pro-projects-error-name-contact",
    en: "A project name cannot contain an email address or a link.",
    nl: "Een projectnaam mag geen e-mailadres of link bevatten.",
    de: "Ein Projektname darf keine E-Mail-Adresse und keinen Link enthalten.",
    fr: "Un nom de projet ne peut pas contenir d'adresse e-mail ni de lien.",
    es: "El nombre de un proyecto no puede contener una dirección de correo electrónico ni un enlace.",
    it: "Il nome di un progetto non può contenere un indirizzo email o un link.",
  },
  errorGeneric: {
    key: "pro-projects-error-generic",
    en: "Something went wrong. Please try again.",
    nl: "Er ging iets mis. Probeer het opnieuw.",
    de: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
    fr: "Une erreur s'est produite. Veuillez réessayer.",
    es: "Algo salió mal. Inténtalo de nuevo.",
    it: "Qualcosa è andato storto. Riprova.",
  },
  addPurchases: {
    key: "pro-projects-add-purchases",
    en: "Add purchases",
    nl: "Aankopen toevoegen",
    de: "Käufe hinzufügen",
    fr: "Ajouter des achats",
    es: "Añadir compras",
    it: "Aggiungi acquisti",
  },
  pickerTitle: {
    key: "pro-projects-picker-title",
    en: "Add purchases to {{project}}",
    nl: "Aankopen toevoegen aan {{project}}",
    de: "Käufe zu {{project}} hinzufügen",
    fr: "Ajouter des achats à {{project}}",
    es: "Añadir compras a {{project}}",
    it: "Aggiungi acquisti a {{project}}",
  },
  pickerTitleLoose: {
    key: "pro-projects-picker-title-loose",
    en: "Add purchases to a project",
    nl: "Aankopen aan een project toevoegen",
    de: "Käufe zu einem Projekt hinzufügen",
    fr: "Ajouter des achats à un projet",
    es: "Añadir compras a un proyecto",
    it: "Aggiungi acquisti a un progetto",
  },
  pickerProjectLabel: {
    key: "pro-projects-picker-project-label",
    en: "Project",
    nl: "Project",
    de: "Projekt",
    fr: "Projet",
    es: "Proyecto",
    it: "Progetto",
  },
  pickerChooseProject: {
    key: "pro-projects-picker-choose-project",
    en: "Choose a project",
    nl: "Kies een project",
    de: "Wähle ein Projekt",
    fr: "Choisissez un projet",
    es: "Elige un proyecto",
    it: "Scegli un progetto",
  },
  pickerSearch: {
    key: "pro-projects-picker-search",
    en: "Search by title",
    nl: "Zoek op titel",
    de: "Nach Titel suchen",
    fr: "Rechercher par titre",
    es: "Buscar por título",
    it: "Cerca per titolo",
  },
  pickerNoMatch: {
    key: "pro-projects-picker-no-match",
    en: 'No purchases found for "{{query}}".',
    nl: 'Geen aankopen gevonden voor "{{query}}".',
    de: 'Keine Käufe gefunden für "{{query}}".',
    fr: 'Aucun achat trouvé pour "{{query}}".',
    es: 'No se encontraron compras para "{{query}}".',
    it: 'Nessun acquisto trovato per "{{query}}".',
  },
  pickerEmpty: {
    key: "pro-projects-picker-empty",
    en: "You have no purchases to add yet.",
    nl: "Je hebt nog geen aankopen die je kunt toevoegen.",
    de: "Du hast noch keine Käufe, die du hinzufügen kannst.",
    fr: "Vous n'avez pas encore d'achats à ajouter.",
    es: "Aún no tienes compras para añadir.",
    it: "Non hai ancora acquisti da aggiungere.",
  },
  pickerInProject: {
    key: "pro-projects-picker-in-project",
    en: "In {{name}}",
    nl: "In {{name}}",
    de: "In {{name}}",
    fr: "Dans {{name}}",
    es: "En {{name}}",
    it: "In {{name}}",
  },
  pickerMoveNote: {
    key: "pro-projects-picker-move-note",
    en: "A purchase belongs to one project. If you choose a purchase from another project, it moves.",
    nl: "Een aankoop hoort bij één project. Kies je een aankoop uit een ander project, dan verhuist hij.",
    de: "Ein Kauf gehört zu einem Projekt. Wählst du einen Kauf aus einem anderen Projekt, wird er verschoben.",
    fr: "Un achat appartient à un seul projet. Si vous choisissez un achat d'un autre projet, il est déplacé.",
    es: "Una compra pertenece a un solo proyecto. Si eliges una compra de otro proyecto, se traslada.",
    it: "Un acquisto appartiene a un solo progetto. Se scegli un acquisto di un altro progetto, viene spostato.",
  },
  pickerConfirmOne: {
    key: "pro-projects-picker-confirm-one",
    en: "Add {{count}} purchase",
    nl: "{{count}} aankoop toevoegen",
    de: "{{count}} Kauf hinzufügen",
    fr: "Ajouter {{count}} achat",
    es: "Añadir {{count}} compra",
    it: "Aggiungi {{count}} acquisto",
  },
  pickerConfirm: {
    key: "pro-projects-picker-confirm",
    en: "Add {{count}} purchases",
    nl: "{{count}} aankopen toevoegen",
    de: "{{count}} Käufe hinzufügen",
    fr: "Ajouter {{count}} achats",
    es: "Añadir {{count}} compras",
    it: "Aggiungi {{count}} acquisti",
  },
  removePurchase: {
    key: "pro-projects-remove-purchase",
    en: "Remove from project",
    nl: "Uit project halen",
    de: "Aus dem Projekt entfernen",
    fr: "Retirer du projet",
    es: "Quitar del proyecto",
    it: "Rimuovi dal progetto",
  },
  purchasesHeading: {
    key: "pro-projects-purchases-heading",
    en: "Purchases",
    nl: "Aankopen",
    de: "Käufe",
    fr: "Achats",
    es: "Compras",
    it: "Acquisti",
  },
  columnPurchase: {
    key: "pro-projects-column-purchase",
    en: "Purchase",
    nl: "Aankoop",
    de: "Kauf",
    fr: "Achat",
    es: "Compra",
    it: "Acquisto",
  },
  columnPrice: {
    key: "pro-projects-column-price",
    en: "Price",
    nl: "Prijs",
    de: "Preis",
    fr: "Prix",
    es: "Precio",
    it: "Prezzo",
  },
  columnPaid: {
    key: "pro-projects-column-paid",
    en: "Paid",
    nl: "Betaald",
    de: "Bezahlt",
    fr: "Payé",
    es: "Pagado",
    it: "Pagato",
  },
  labelProBuyer: {
    key: "pro-projects-label-pro-buyer",
    en: "Pro Buyer price {{pct}}%",
    nl: "Pro Buyer-prijs {{pct}}%",
    de: "Pro Buyer-Preis {{pct}}%",
    fr: "Prix Pro Buyer {{pct}}%",
    es: "Precio Pro Buyer {{pct}}%",
    it: "Prezzo Pro Buyer {{pct}}%",
  },
  labelRegular: {
    key: "pro-projects-label-regular",
    en: "Regular price",
    nl: "Gewone prijs",
    de: "Normaler Preis",
    fr: "Prix normal",
    es: "Precio normal",
    it: "Prezzo normale",
  },
  labelBid: {
    key: "pro-projects-label-bid",
    en: "Bid",
    nl: "Bod",
    de: "Gebot",
    fr: "Offre",
    es: "Oferta",
    it: "Offerta",
  },
  none: {
    key: "pro-projects-none",
    en: "None",
    nl: "Geen",
    de: "Keine",
    fr: "Aucune",
    es: "Ninguno",
    it: "Nessuno",
  },
  listHelp: {
    key: "pro-projects-list-help",
    en: "Amounts are what you paid for the item. A row opens the purchase in Purchases.",
    nl: "Bedragen zijn wat je voor het artikel betaalde. Een rij opent de aankoop in Aankopen.",
    de: "Beträge sind das, was du für den Artikel bezahlt hast. Eine Zeile öffnet den Kauf unter Käufe.",
    fr: "Les montants correspondent à ce que vous avez payé pour l'article. Une ligne ouvre l'achat dans Achats.",
    es: "Los importes son lo que pagaste por el artículo. Una fila abre la compra en Compras.",
    it: "Gli importi sono quanto hai pagato per l'articolo. Una riga apre l'acquisto in Acquisti.",
  },
  filterAll: {
    key: "pro-projects-filter-all",
    en: "All",
    nl: "Alles",
    de: "Alle",
    fr: "Tout",
    es: "Todo",
    it: "Tutti",
  },
  filterProBuyer: {
    key: "pro-projects-filter-pro-buyer",
    en: "At Pro Buyer price",
    nl: "Met Pro Buyer-prijs",
    de: "Mit Pro Buyer-Preis",
    fr: "Au prix Pro Buyer",
    es: "Con precio Pro Buyer",
    it: "Con prezzo Pro Buyer",
  },
  filterNoDiscount: {
    key: "pro-projects-filter-no-discount",
    en: "Without discount",
    nl: "Zonder korting",
    de: "Ohne Rabatt",
    fr: "Sans remise",
    es: "Sin descuento",
    it: "Senza sconto",
  },
  projectEmpty: {
    key: "pro-projects-project-empty",
    en: "No purchases in this project yet.",
    nl: "Nog geen aankopen in dit project.",
    de: "Noch keine Käufe in diesem Projekt.",
    fr: "Pas encore d'achats dans ce projet.",
    es: "Aún no hay compras en este proyecto.",
    it: "Ancora nessun acquisto in questo progetto.",
  },
  statusExpired: {
    key: "pro-projects-status-expired",
    en: "Expired",
    nl: "Verlopen",
    de: "Abgelaufen",
    fr: "Expiré",
    es: "Caducado",
    it: "Scaduto",
  },
  statusRefunded: {
    key: "pro-projects-status-refunded",
    en: "Refunded",
    nl: "Terugbetaald",
    de: "Erstattet",
    fr: "Remboursé",
    es: "Reembolsado",
    it: "Rimborsato",
  },
  notCounted: {
    key: "pro-projects-not-counted",
    en: "Not counted",
    nl: "Telt niet mee",
    de: "Zählt nicht mit",
    fr: "Non comptabilisé",
    es: "No cuenta",
    it: "Non conteggiato",
  },
  backToProjects: {
    key: "pro-projects-back",
    en: "All projects",
    nl: "Alle projecten",
    de: "Alle Projekte",
    fr: "Tous les projets",
    es: "Todos los proyectos",
    it: "Tutti i progetti",
  },
  notFound: {
    key: "pro-projects-not-found",
    en: "This project no longer exists.",
    nl: "Dit project bestaat niet meer.",
    de: "Dieses Projekt existiert nicht mehr.",
    fr: "Ce projet n'existe plus.",
    es: "Este proyecto ya no existe.",
    it: "Questo progetto non esiste più.",
  },
  // WHO-5959 F15: tells two purchases with the same title apart in the picker.
  orderNumber: {
    key: "pro-projects-order-number",
    en: "Order {{number}}",
    nl: "Bestelling {{number}}",
    de: "Bestellung {{number}}",
    fr: "Commande {{number}}",
    es: "Pedido {{number}}",
    it: "Ordine {{number}}",
  },
  boughtOn: {
    key: "pro-projects-bought-on",
    en: "Bought on {{date}}",
    nl: "Gekocht op {{date}}",
    de: "Gekauft am {{date}}",
    fr: "Acheté le {{date}}",
    es: "Comprado el {{date}}",
    it: "Acquistato il {{date}}",
  },
  menuAddToProject: {
    key: "pro-projects-menu-add-to-project",
    en: "Add to project",
    nl: "Aan project toevoegen",
    de: "Zu Projekt hinzufügen",
    fr: "Ajouter à un projet",
    es: "Añadir a un proyecto",
    it: "Aggiungi a un progetto",
  },
  menuChangeProject: {
    key: "pro-projects-menu-change-project",
    en: "Change project",
    nl: "Project wijzigen",
    de: "Projekt ändern",
    fr: "Changer de projet",
    es: "Cambiar proyecto",
    it: "Cambia progetto",
  },
  panelChange: {
    key: "pro-projects-panel-change",
    en: "Change",
    nl: "Wijzigen",
    de: "Ändern",
    fr: "Modifier",
    es: "Cambiar",
    it: "Modifica",
  },
  panelPrivacy: {
    key: "pro-projects-panel-privacy",
    en: "Only you see your projects. The seller does not see them.",
    nl: "Alleen jij ziet je projecten. De verkoper ziet ze niet.",
    de: "Nur du siehst deine Projekte. Der Verkäufer sieht sie nicht.",
    fr: "Vous seul voyez vos projets. Le vendeur ne les voit pas.",
    es: "Solo tú ves tus proyectos. El vendedor no los ve.",
    it: "Solo tu vedi i tuoi progetti. Il venditore non li vede.",
  },
  chooserAssigned: {
    key: "pro-projects-chooser-assigned",
    en: "Added to {{project}}",
    nl: "Toegevoegd aan {{project}}",
    de: "Zu {{project}} hinzugefügt",
    fr: "Ajouté à {{project}}",
    es: "Añadido a {{project}}",
    it: "Aggiunto a {{project}}",
  },
  chooserRemoved: {
    key: "pro-projects-chooser-removed",
    en: "Removed from the project",
    nl: "Uit het project gehaald",
    de: "Aus dem Projekt entfernt",
    fr: "Retiré du projet",
    es: "Quitado del proyecto",
    it: "Rimosso dal progetto",
  },
  dashboardLink: {
    key: "pro-projects-dashboard-link",
    en: "Go to your Pro Buyer projects",
    nl: "Naar je Pro Buyer-projecten",
    de: "Zu deinen Pro Buyer-Projekten",
    fr: "Vers vos projets Pro Buyer",
    es: "Ir a tus proyectos Pro Buyer",
    it: "Vai ai tuoi progetti Pro Buyer",
  },
  allInvoices: {
    key: "pro-projects-all-invoices",
    en: "All invoices ({{count}})",
    nl: "Alle facturen ({{count}})",
    de: "Alle Rechnungen ({{count}})",
    fr: "Toutes les factures ({{count}})",
    es: "Todas las facturas ({{count}})",
    it: "Tutte le fatture ({{count}})",
  },
  invoicesPart: {
    key: "pro-projects-invoices-part",
    en: "Invoices {{from}} to {{to}}",
    nl: "Facturen {{from}} tot {{to}}",
    de: "Rechnungen {{from}} bis {{to}}",
    fr: "Factures {{from}} à {{to}}",
    es: "Facturas {{from}} a {{to}}",
    it: "Fatture da {{from}} a {{to}}",
  },
  invoicesSplitHelp: {
    key: "pro-projects-invoices-split-help",
    en: "More than 50 invoices: you download them in parts of 50.",
    nl: "Meer dan 50 facturen: je downloadt ze in delen van 50.",
    de: "Mehr als 50 Rechnungen: Du lädst sie in Teilen von 50 herunter.",
    fr: "Plus de 50 factures : vous les téléchargez par lots de 50.",
    es: "Más de 50 facturas: las descargas en partes de 50.",
    it: "Più di 50 fatture: le scarichi in parti da 50.",
  },
  invoicesNone: {
    key: "pro-projects-invoices-none",
    en: "No invoices available yet.",
    nl: "Nog geen facturen beschikbaar.",
    de: "Noch keine Rechnungen verfügbar.",
    fr: "Aucune facture disponible pour le moment.",
    es: "Todavía no hay facturas disponibles.",
    it: "Nessuna fattura ancora disponibile.",
  },
  invoicesFileName: {
    key: "pro-projects-invoices-file-name",
    en: "{{project}} invoices",
    nl: "{{project}} facturen",
    de: "{{project}} Rechnungen",
    fr: "{{project}} factures",
    es: "{{project}} facturas",
    it: "{{project}} fatture",
  },
  // WHO-5959 6e: reservations in Pro Buyer-projecten (spec S2, S3, S8, S9).
  reserveProjectLabel: {
    key: "pro-projects-reserve-project-label",
    en: "Which project are you reserving this for? (optional)",
    nl: "Voor welk project reserveer je dit? (optioneel)",
    de: "Für welches Projekt reservierst du das? (optional)",
    fr: "Pour quel projet réservez-vous cette pièce ? (facultatif)",
    es: "¿Para qué proyecto lo reservas? (opcional)",
    it: "Per quale progetto lo riservi? (facoltativo)",
  },
  reserveProjectNone: {
    key: "pro-projects-reserve-project-none",
    en: "No project",
    nl: "Geen project",
    de: "Kein Projekt",
    fr: "Aucun projet",
    es: "Sin proyecto",
    it: "Nessun progetto",
  },
  reservedHeading: {
    key: "pro-projects-reserved-heading",
    en: "Reserved",
    nl: "Gereserveerd",
    de: "Reserviert",
    fr: "Réservé",
    es: "Reservado",
    it: "Riservato",
  },
  reservedFigureSub: {
    key: "pro-projects-reserved-figure-sub",
    en: "{{count}} reserved · {{hours}} h left",
    nl: "{{count}} gereserveerd · nog {{hours}} uur",
    de: "{{count}} reserviert · noch {{hours}} Std.",
    fr: "{{count}} réservé(s) · encore {{hours}} h",
    es: "{{count}} reservado(s) · quedan {{hours}} h",
    it: "{{count}} riservato/i · ancora {{hours}} h",
  },
  reservationClock: {
    key: "pro-projects-reservation-clock",
    en: "{{hours}} h left · until {{date}}",
    nl: "Nog {{hours}} uur · tot {{date}}",
    de: "Noch {{hours}} Std. · bis {{date}}",
    fr: "Encore {{hours}} h · jusqu'au {{date}}",
    es: "Quedan {{hours}} h · hasta el {{date}}",
    it: "Ancora {{hours}} h · fino a {{date}}",
  },
  reservationCheckout: {
    key: "pro-projects-reservation-checkout",
    en: "Check out",
    nl: "Afrekenen",
    de: "Zur Kasse",
    fr: "Payer",
    es: "Pagar",
    it: "Paga",
  },
  reservationRelease: {
    key: "pro-projects-reservation-release",
    en: "Release",
    nl: "Vrijgeven",
    de: "Freigeben",
    fr: "Libérer",
    es: "Liberar",
    it: "Rilascia",
  },
  reservationReleaseTitle: {
    key: "pro-projects-reservation-release-title",
    en: "Release this reservation?",
    nl: "Deze reservering vrijgeven?",
    de: "Diese Reservierung freigeben?",
    fr: "Libérer cette réservation ?",
    es: "¿Liberar esta reserva?",
    it: "Rilasciare questa prenotazione?",
  },
  reservationReleaseBody: {
    key: "pro-projects-reservation-release-body",
    en: "The piece is for sale to everyone again straight away and your fixed price lapses.",
    nl: "Het stuk is direct weer voor iedereen te koop en je vaste prijs vervalt.",
    de: "Das Stück ist sofort wieder für alle zu kaufen und dein fester Preis entfällt.",
    fr: "La pièce est de nouveau en vente pour tous immédiatement et votre prix fixe expire.",
    es: "La pieza vuelve a estar a la venta para todos al instante y tu precio fijo caduca.",
    it: "Il pezzo torna subito in vendita per tutti e il tuo prezzo fisso decade.",
  },
  reservationWaiting: {
    key: "pro-projects-reservation-waiting",
    en: "Waiting for the seller · request expires in {{hours}} h",
    nl: "Wacht op de verkoper · aanvraag verloopt over {{hours}} uur",
    de: "Wartet auf den Verkäufer · Anfrage läuft in {{hours}} Std. ab",
    fr: "En attente du vendeur · la demande expire dans {{hours}} h",
    es: "Esperando al vendedor · la solicitud caduca en {{hours}} h",
    it: "In attesa del venditore · la richiesta scade tra {{hours}} h",
  },
  reservationViewProduct: {
    key: "pro-projects-reservation-view-product",
    en: "View product",
    nl: "Bekijk product",
    de: "Produkt ansehen",
    fr: "Voir le produit",
    es: "Ver producto",
    it: "Vedi prodotto",
  },
  reservationFixedPrice: {
    key: "pro-projects-reservation-fixed-price",
    en: "Fixed price",
    nl: "Vaste prijs",
    de: "Fester Preis",
    fr: "Prix fixe",
    es: "Precio fijo",
    it: "Prezzo fisso",
  },
  cardReservedChip: {
    key: "pro-projects-card-reserved-chip",
    en: "{{count}} reserved",
    nl: "{{count}} gereserveerd",
    de: "{{count}} reserviert",
    fr: "{{count}} réservé(s)",
    es: "{{count}} reservado(s)",
    it: "{{count}} riservato/i",
  },
  dashboardReservationProject: {
    key: "pro-projects-dashboard-reservation-project",
    en: "For {{project}}",
    nl: "Voor {{project}}",
    de: "Für {{project}}",
    fr: "Pour {{project}}",
    es: "Para {{project}}",
    it: "Per {{project}}",
  },
  removedToast: {
    key: "pro-projects-removed-toast",
    en: "Removed from {{project}}",
    nl: "Uit {{project}} gehaald",
    de: "Aus {{project}} entfernt",
    fr: "Retiré de {{project}}",
    es: "Quitado de {{project}}",
    it: "Rimosso da {{project}}",
  },
  undoRemove: {
    key: "pro-projects-undo-remove",
    en: "Undo",
    nl: "Ongedaan maken",
    de: "Rückgängig machen",
    fr: "Annuler",
    es: "Deshacer",
    it: "Annulla",
  },
} satisfies Record<string, Locales & { key: string }>;

export type ProProjectsCopyKey = keyof typeof PRO_PROJECTS_COPY;

/** i18next namespace the keys live in (Lokalise filename `account.json`). */
export const PRO_PROJECTS_NAMESPACE = "account";

/**
 * WHO-5959 F17. The copy the reserve modal on the product page reads: the
 * project field (`ReservationProjectField`) and the "+ Nieuw project" name
 * dialog (`ProProjectNameDialog`, with every error it can show). The PDP does
 * not load the whole `account` namespace (page weight), only these keys of it;
 * a key missing here renders in English on the product page.
 */
export const PRO_PROJECTS_PDP_COPY_KEYS = [
  "reserveProjectLabel",
  "reserveProjectNone",
  "newProject",
  "panelPrivacy",
  "createConfirm",
  "cancel",
  "nameLabel",
  "nameHelp",
  "errorNameEmpty",
  "errorNameInvalid",
  "errorNameTaken",
  "errorNameContact",
  "errorGeneric",
] as const satisfies readonly ProProjectsCopyKey[];

/** The Lokalise keys of `PRO_PROJECTS_PDP_COPY_KEYS`, as the PDP requests them. */
export const PRO_PROJECTS_PDP_LOKALISE_KEYS = PRO_PROJECTS_PDP_COPY_KEYS.map(
  copyKey => PRO_PROJECTS_COPY[copyKey].key
);

export { PRO_BUYER_PROJECTS_UI_GATE } from "@/features/pro-buyer-projects/static/pro-buyer-projects.gate";
